/**
 * Niente moduli di accesso estranei dentro la finestra di una lezione.
 *
 * Segnalazione del cliente (2026-08-26): «quando vengo disconnesso dall'account
 * e sono dentro al player mi appare la finestra del login di edx.. non va bene
 * - la modal dovrebbe chiudersi e dovrebbe mandarmi verso l'sso».
 *
 * Su questo progetto l'accesso passa SOLO da Keycloak (DEC-040): nel sito non
 * esiste un modulo di accesso proprio, e quello nativo di Open edX non deve
 * comparire mai dentro il player.
 *
 * ---------------------------------------------------------------------------
 * COSA SI E' OSSERVATO, NON DEDOTTO (dev, 2026-08-26)
 * ---------------------------------------------------------------------------
 *
 * Aprendo `learning.dev.igeacps.click/course/course-v1:IGEA+DSA101+2026/home`
 * senza sessione LMS, il player NON naviga da nessuna parte: disegna la propria
 * pagina-cancello, con due avvisi —
 *
 *   «To see course content, sign in or register.»          (LogistrationAlert)
 *   «You must be enrolled in the course to see course
 *    content. Sign in or register and then enroll…»        (PrivateCourseAlert)
 *
 * — e i link «sign in» di tutti e due puntano a
 * `https://lms.dev.igeacps.click/login?next=…`, cioe' al modulo NATIVO di Open
 * edX («Accedi», Email + Password, «Nuovo utente? Creare un account.»). E'
 * esattamente la finestra che il cliente non deve vedere.
 *
 * Da qui due conseguenze, e due cure diverse in questo stesso file.
 *
 * 1. LA CAUSA CHE MORDE OGGI e' la pagina-cancello, non una navigazione. La
 *    condizione che la fa comparire e' una sola e osservabile: `authenticatedUser
 *    === null` (e' la stessa che accende i due avvisi, vedi
 *    `alerts/logistration-alert/hooks.js` e `alerts/enrollment-alert/hooks.js`).
 *    Dentro il player un utente anonimo non e' uno stato legittimo: la modale si
 *    apre solo per chi e' iscritto. Quindi anonimo + dentro un iframe = la
 *    sessione LMS non c'e' piu'.
 *
 * 2. `redirectToLogin` di `@edx/frontend-platform` — la navigazione automatica
 *    verso `LOGIN_URL` — in questo MFE, MISURATO, non parte mai: l'unico
 *    chiamante automatico e' `ensureAuthenticatedUser`, che gira solo con
 *    `requireAuthenticatedUser: true`, che `src/index.jsx` non imposta; e
 *    `AuthenticatedPageRoute`/`LoginRedirect` non sono usati da nessuna rotta.
 *    L'intercettazione resta comunque, perche' e' l'unico varco da cui quella
 *    navigazione potrebbe passare se un domani venisse riacceso o se una
 *    libreria la invocasse: costa poco e chiude la porta prima che si apra. Ma
 *    da sola non avrebbe risolto niente, ed e' giusto che si sappia.
 *
 * ---------------------------------------------------------------------------
 * PERCHE' UNA PAGINA LENTA NON DIVENTA UNA DISCONNESSIONE
 * ---------------------------------------------------------------------------
 *
 * `utente === null` non e' un timeout ne' un'ipotesi: e' un verdetto del
 * server. In `AxiosJwtTokenService` il rinnovo del token restituisce `null`
 * SOLO su una risposta 401; qualunque altro guasto — rete assente, 500,
 * richiesta appesa — viene rilanciato, e `initialize()` finisce in
 * `APP_INIT_ERROR`, non in «anonimo». Percio' qui non serve, e non si aggiunge,
 * nessun conto alla rovescia: non si ripete il difetto gia' noto in cui un
 * caricamento che non finisce mai e' indistinguibile da un guasto.
 *
 * Per la stessa ragione si confronta con `null` e non con un valore falso
 * qualsiasi: `undefined` vuol dire «non lo sappiamo ancora», e l'ignoranza non
 * va scambiata per una caduta.
 *
 * ---------------------------------------------------------------------------
 * IL CAMPANELLO
 * ---------------------------------------------------------------------------
 *
 * `{ type: 'igea:sessione-scaduta' }`, senza dati, sulla falsariga di
 * `igea:quiz-unit`: e' un campanello, non un canale. Il destinatario e' il sito
 * (`onMessaggioPlayer` in `CoursePlayerModal.vue`), che chiude la modale, salva
 * l'ultima posizione e manda all'SSO rientrando sulla lezione. Qui non si
 * decide niente di tutto questo — il player non sa dove sta l'SSO, e non deve.
 *
 * Fuori da un iframe non si suona e non si cambia niente: il player aperto
 * direttamente in una scheda deve conservare il comportamento normale di
 * accesso di Open edX.
 */

export const MESSAGGIO_SESSIONE_SCADUTA = 'igea:sessione-scaduta';

/**
 * Siamo dentro un iframe?
 *
 * `finestra.parent` si puo' leggere e confrontare anche fra origini diverse:
 * il riferimento e' sempre accessibile, sono i suoi contenuti a non esserlo.
 *
 * In caso di dubbio si risponde «no»: significa lasciare il comportamento
 * normale di Open edX, che e' il ripiego innocuo.
 */
export function dentroIframe(finestra = global) {
  try {
    return Boolean(finestra) && finestra.parent != null && finestra.parent !== finestra;
  } catch (e) {
    return false;
  }
}

/**
 * Un campanello che suona al massimo una volta.
 *
 * La memoria vive nella chiusura, non in un modulo: cosi' ogni prova si
 * costruisce il suo senza bisogno di una funzione di azzeramento — una porta
 * di servizio aperta solo per i test e' una porta aperta.
 *
 * Suonare due volte manderebbe il sito all'SSO mentre ci sta gia' andando.
 */
export function creaCampanello(finestra = global) {
  let suonato = false;

  return function suona() {
    if (suonato) { return false; }
    if (!dentroIframe(finestra)) { return false; }
    suonato = true;
    finestra.parent.postMessage({ type: MESSAGGIO_SESSIONE_SCADUTA }, '*');
    return true;
  };
}

/**
 * La sessione LMS e' caduta sotto i piedi di chi sta studiando?
 *
 * Dentro il player, e senza utente. Le due condizioni servono entrambe: fuori
 * dal player l'anonimo e' un visitatore, dentro il player e' un guasto.
 */
export function sessioneCadutaNelPlayer({ finestra = global, utente }) {
  return dentroIframe(finestra) && utente === null;
}

/**
 * Chiude il varco della navigazione automatica verso `LOGIN_URL`.
 *
 * Ci si ferma in base a DOVE siamo, non a se il campanello ha gia' suonato:
 * legare le due cose vorrebbe dire che, dopo la prima suonata, una seconda
 * chiamata finirebbe dritta sul modulo di accesso di edX — proprio quello che
 * si sta togliendo di mezzo.
 */
export function intercettaAccessoEstraneo({ servizio, finestra = global, suona }) {
  const originale = servizio.redirectToLogin.bind(servizio);

  // eslint-disable-next-line no-param-reassign
  servizio.redirectToLogin = (indirizzo) => {
    if (dentroIframe(finestra)) {
      suona();
      return undefined;
    }
    return originale(indirizzo);
  };

  return servizio;
}

/**
 * L'unico ingresso: monta la guardia e dice se la lezione si puo' mostrare.
 *
 * `lezioneMostrabile: false` vuol dire che al posto delle rotte va disegnato un
 * segnaposto neutro. Continuare a disegnarle mostrerebbe la pagina-cancello con
 * i suoi link al modulo di accesso di edX per tutto il tempo che il sito impiega
 * a portarci all'SSO: breve, ma e' precisamente la cosa segnalata.
 */
export function guardiaSessionePlayer({ finestra = global, servizio, utente }) {
  const suona = creaCampanello(finestra);

  if (servizio) {
    intercettaAccessoEstraneo({ servizio, finestra, suona });
  }

  const caduta = sessioneCadutaNelPlayer({ finestra, utente });
  if (caduta) { suona(); }

  return { lezioneMostrabile: !caduta };
}
